import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * Generic hook to get a query parameter from the URL
 * @param paramName - The name of the query parameter
 * @param defaultValue - Value to return if parameter is not found
 */
export function useQueryParam(paramName: string, defaultValue = ''): string {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(paramName) ?? defaultValue;
  }, [location.search, paramName, defaultValue]);
}

/**
 * Specific hook for patientId query parameter
 */
export function usePatientId(): string {
  return useQueryParam('patientId', '');
}

/**
 * Specific hook for token query parameter (for embedded iframe context)
 */
export function useToken(): string {
  return useQueryParam('token', '');
}

/**
 * Hook to get theme colors from URL parameters
 */
export function useThemeColors(): Record<string, string> {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const themeParam = params.get('themeColors');
    if (themeParam) {
      try {
        // Parse the JSON theme parameter
        const themeColors = JSON.parse(themeParam);
        return themeColors;
      } catch (error) {
        console.warn('Failed to parse theme colors from URL parameter:', error);
        return {};
      }
    }
    
    return {};
  }, [location.search]);
}
